/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WASP Frontend v2.4.0 - ScheduleEventModal
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file ScheduleEventModal.tsx
 * @description Modal wizard para crear/editar envíos programados
 * @version 1.0.0
 * @date 2025-01-27
 * 
 * WIZARD STEPS:
 * 1. Tipo - Envío único o campaña recurrente
 * 2. Fecha/Hora - DatePicker + TimePicker + TimeZone
 * 3. Destinatarios - Selector de contactos
 * 4. Mensaje - Texto + Media opcional
 * 5. Canal - WhatsApp/Instagram/Facebook/Telegram
 * 6. Confirmación - Preview completo antes de crear
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Button, Form, Alert, Badge, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faCheck,
  faTimes,
  faCalendar,
  faClock,
  faUsers,
  faEnvelope,
  faRepeat,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import {
  faWhatsapp,
  faInstagram,
  faFacebookF,
  faTelegram
} from '@fortawesome/free-brands-svg-icons';

// Components
import TimeZoneSelector from './TimeZoneSelector';
import DateTimePicker from './DateTimePicker';
import ContactSelectorModal from './ContactSelectorModal';
import MessageComposer from './MessageComposer';

// Hooks
import { useScheduler } from '../../../hooks/useScheduler';
import { useRecurringCampaigns } from '../../../hooks/useRecurringCampaigns';
import { useTimeZone } from '../../../hooks/useTimeZone';
import { useContactsDatabase } from '../../../hooks/useContactsDatabase';

// Types
import { 
  ScheduledMessage, 
  ChannelType,
  RecurrencePattern,
  ScheduleFormData,
  RecurrenceConfig
} from '../../../types/scheduler.types';

// Utils
import { formatDateSpanish, isValidDateString, isValidTimeString } from '../../../types/dateUtils';
import { enrichGroups, enrichTags } from '../../../types/scheduler-shared.types';
import { getHatateToken } from 'helpers/utils';
import { useUserContext } from 'layouts/UserContext';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface ScheduleEventModalProps {
  show: boolean;
  onHide: () => void;
  event?: ScheduledMessage | null;
  preselectedDate?: Date | null;
  onSave?: () => void;
}

type StepId = 1 | 2 | 3 | 4 | 5 | 6;

interface Step {
  id: StepId;
  title: string;
  icon: any;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const ScheduleEventModal: React.FC<ScheduleEventModalProps> = ({
  show,
  onHide,
  event,
  preselectedDate,
  onSave
}) => {
  // ═══════════════════════════════════════════════════════════════════════
  // HOOKS
  // ═══════════════════════════════════════════════════════════════════════
  const { user } = useUserContext();
  const { createSchedule } = useScheduler();
  const { createCampaign } = useRecurringCampaigns();
  const { 
    currentTimezone, 
    validateScheduleTime, 
    generatePreview
  } = useTimeZone();
  
  const {
    contacts,
    groups: rawGroups,
    tags: rawTags,
    selectedContacts: selectedContactsArray,
    toggleContactSelection,
    selectAll,
    clearSelection,
    getSelectedContactsData
  } = useContactsDatabase();
  
  // Convertir array a Set para compatibilidad con ContactSelectorModal
  const selectedContactIds = new Set(selectedContactsArray);
  
  // Enriquecer grupos y tags con count
  const groups = enrichGroups(rawGroups, contacts);
  const tags = enrichTags(rawTags, contacts);

  // ═══════════════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════════════
  
  const [currentStep, setCurrentStep] = useState<StepId>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Estados para modales
  const [showContactModal, setShowContactModal] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState<Date | null>(null);

  // Form data
  const [formData, setFormData] = useState<ScheduleFormData>({
    type: 'single',
    channel: 'whatsapp',
    recipients: {
      contactIds: [],
      count: 0
    },
    message: {
      text: ''
    },
    schedule: {
      date: '',
      time: '',
      timezone: currentTimezone
    }
  });

  // Recurrence data
  const [recurrenceData, setRecurrenceData] = useState<RecurrenceConfig>({
    pattern: 'daily',
    interval: 1,
    endCondition: {
      type: 'never'
    }
  });

  const [campaignName, setCampaignName] = useState('');

  // ═══════════════════════════════════════════════════════════════════════
  // STEPS CONFIGURATION
  // ═══════════════════════════════════════════════════════════════════════
  
  const steps: Step[] = [
    { id: 1, title: 'Tipo', icon: faRepeat },
    { id: 2, title: 'Fecha/Hora', icon: faCalendar },
    { id: 3, title: 'Destinatarios', icon: faUsers },
    { id: 4, title: 'Mensaje', icon: faEnvelope },
    { id: 5, title: 'Canal', icon: faWhatsapp },
    { id: 6, title: 'Confirmación', icon: faCheck }
  ];

  // ═══════════════════════════════════════════════════════════════════════
  // EFFECTS
  // ═══════════════════════════════════════════════════════════════════════
  
  useEffect(() => {
    if (show) {
      // Reset form when modal opens
      setCurrentStep(1);
      setError(null);
      
      // Set preselected date if provided
      if (preselectedDate && !event) {
        // Usar fecha LOCAL, no UTC
        const year = preselectedDate.getFullYear();
        const month = String(preselectedDate.getMonth() + 1).padStart(2, '0');
        const day = String(preselectedDate.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        setFormData(prev => ({
          ...prev,
          schedule: {
            ...prev.schedule,
            date: dateStr
          }
        }));
      }

      // Load event data if editing
      if (event) {
        setFormData({
          type: 'single',
          channel: event.channel,
          recipients: event.recipients,
          message: event.message,
          schedule: {
            date: event.schedule.localDate,
            time: event.schedule.localTime,
            timezone: event.schedule.timezone
          }
        });
      }
    }
  }, [show, event, preselectedDate]);

  // ═══════════════════════════════════════════════════════════════════════
  // VALIDATION
  // ═══════════════════════════════════════════════════════════════════════
  
  const validateStep = (step: StepId): boolean => {
    setError(null);

    switch (step) {
      case 1:
        return true; // Type always valid

      case 2:
        if (!formData.schedule.date) {
          setError('Selecciona una fecha');
          return false;
        }
        if (!isValidDateString(formData.schedule.date)) {
          setError('Formato de fecha inválido');
          return false;
        }
        if (!formData.schedule.time) {
          setError('Selecciona una hora');
          return false;
        }
        if (!isValidTimeString(formData.schedule.time)) {
          setError('Formato de hora inválido (HH:mm)');
          return false;
        }

        // Validate not in past
        const validation = validateScheduleTime(
          formData.schedule.date,
          formData.schedule.time,
          formData.schedule.timezone
        );
        
        if (!validation.valid) {
          setError(validation.message || 'Fecha/hora inválida');
          return false;
        }

        return true;

      case 3:
        if (formData.recipients.count === 0) {
          setError('Selecciona al menos un destinatario');
          return false;
        }
        return true;

      case 4:
        if (!formData.message.text.trim()) {
          setError('Escribe un mensaje');
          return false;
        }
        if (formData.message.text.length > 1000) {
          setError('El mensaje es muy largo (máx 1000 caracteres)');
          return false;
        }
        return true;

      case 5:
        if (!formData.channel) {
          setError('Selecciona un canal');
          return false;
        }
        return true;

      case 6:
        if (formData.type === 'recurring' && !campaignName.trim()) {
          setError('Ingresa un nombre para la campaña');
          return false;
        }
        return true;

      default:
        return true;
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════
  
  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 6) {
        setCurrentStep((prev) => (prev + 1) as StepId);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => (prev - 1) as StepId);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(6)) return;

    try {
      setIsSubmitting(true);
      setError(null);

      if (formData.type === 'single') {
        // Create single scheduled message
        const localDateTime = `${formData.schedule.date}T${formData.schedule.time}:00`;
        
        // 🔥 CRÍTICO: Convertir contactIds (UUIDs) a JIDs de WhatsApp
        // Y guardar datos del contacto para reemplazo de variables
        const contactJids: string[] = [];
        const contactsData: Record<string, any> = {}; // Map JID → contact data
        
        formData.recipients.contactIds.forEach(contactId => {
          // Buscar el contacto por ID
          const contact = contacts.find(c => c.id === contactId);
          if (!contact) {
            console.warn(`Contacto no encontrado: ${contactId}`);
            return;
          }
          
          // Convertir phone a JID de WhatsApp
          const phone = contact.phone;
          if (!phone) {
            console.warn(`Contacto sin teléfono: ${contactId}`);
            return;
          }
          
          // Asegurar formato correcto: +56912345678 → 56912345678@s.whatsapp.net
          const cleanPhone = phone.replace(/[^0-9]/g, '');
          const jid = `${cleanPhone}@s.whatsapp.net`;
          
          console.log(`Contacto ${contactId} → JID: ${jid}`);
          
          contactJids.push(jid);
          
          // Guardar datos del contacto para reemplazo de variables
          contactsData[jid] = {
            nombre: contact.name || '',
            name: contact.name || '',
            email: contact.email || '',
            empresa: contact.company || '',
            company: contact.company || '',
            cargo: contact.positon || '',
            position: contact.positon || '',
            telefono: phone,
            phone: phone
          };
        });
        
        if (contactJids.length === 0) {
          throw new Error('No se pudieron convertir los contactos a JIDs de WhatsApp');
        }
        
        const scheduleData = {
          phoneId: user._id,
          type: 'single' as const,
          channel: formData.channel,
          recipients: {
            contactIds: contactJids, // ✅ Usar JIDs en lugar de UUIDs
            count: contactJids.length,
            contactsData: contactsData // ✅ Datos para reemplazo de variables
          },
          message: formData.message,
          schedule: {
            scheduledAt: localDateTime,
            timezone: formData.schedule.timezone,
            localTime: formData.schedule.time,
            localDate: formData.schedule.date,
            date: formData.schedule.date,
            time: formData.schedule.time
          },
          metadata: {
            createdAt: new Date().toISOString(),
            createdBy: user._id
          }
        };
        
        console.log('📡 Creando schedule con data:', scheduleData);
        
        const result = await createSchedule(scheduleData);
        
        console.log('✅ Schedule creado:', result);
      } else {
        // Create recurring campaign
        await createCampaign({
          name: campaignName,
          channel: formData.channel,
          recipients: formData.recipients,
          message: formData.message,
          schedule: {
            startTime: formData.schedule.time,
            timezone: formData.schedule.timezone,
            recurrence: recurrenceData
          },
          stats: {
            totalRuns: 0,
            successfulRuns: 0,
            failedRuns: 0
          },
          metadata: {
            createdAt: new Date().toISOString(),
            createdBy: user._id
          }
        });
      }

      onSave?.();
      onHide();
    } catch (err) {
      console.error('❌ Error creando schedule:', err);
      setError(err instanceof Error ? err.message : 'Error al crear envío');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onHide();
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER PROGRESS
  // ═══════════════════════════════════════════════════════════════════════
  
  const renderProgress = () => {
    const progress = ((currentStep - 1) / (steps.length - 1)) * 100;

    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between mb-2">
          {steps.map((step) => (
            <div
              key={step.id}
              style={{
                flex: 1,
                textAlign: 'center',
                opacity: step.id <= currentStep ? 1 : 0.4
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: step.id <= currentStep ? '#2ca799' : 'var(--bs-secondary-bg)',
                  color: step.id <= currentStep ? 'white' : 'var(--bs-secondary-color)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '8px'
                }}
              >
                <FontAwesomeIcon icon={step.icon} size="sm" />
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>
                {step.title}
              </div>
            </div>
          ))}
        </div>
        <ProgressBar 
          now={progress} 
          style={{ height: '4px', backgroundColor: 'var(--bs-secondary-bg)' }}
        >
          <ProgressBar 
            now={progress}
            style={{ backgroundColor: '#2ca799' }}
          />
        </ProgressBar>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER STEP 1: TYPE
  // ═══════════════════════════════════════════════════════════════════════
  
  const renderStep1 = () => (
    <div>
      <h5 className="mb-3">Tipo de Envío</h5>
      
      <div className="d-flex gap-3">
        <div
          onClick={() => setFormData({ ...formData, type: 'single' })}
          style={{
            flex: 1,
            padding: '20px',
            borderRadius: '12px',
            border: `2px solid ${formData.type === 'single' ? '#2ca799' : 'var(--bs-border-color)'}`,
            backgroundColor: formData.type === 'single' ? 'rgba(44, 167, 153, 0.1)' : 'var(--bs-body-bg)',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <FontAwesomeIcon 
            icon={faClock} 
            size="2x" 
            style={{ color: formData.type === 'single' ? '#2ca799' : 'var(--bs-secondary-color)' }}
            className="mb-2"
          />
          <h6>Envío Único</h6>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            Programa un envío para una fecha y hora específica
          </p>
        </div>

        <div
          onClick={() => setFormData({ ...formData, type: 'recurring' })}
          style={{
            flex: 1,
            padding: '20px',
            borderRadius: '12px',
            border: `2px solid ${formData.type === 'recurring' ? '#2ca799' : 'var(--bs-border-color)'}`,
            backgroundColor: formData.type === 'recurring' ? 'rgba(44, 167, 153, 0.1)' : 'var(--bs-body-bg)',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <FontAwesomeIcon 
            icon={faRepeat} 
            size="2x" 
            style={{ color: formData.type === 'recurring' ? '#2ca799' : 'var(--bs-secondary-color)' }}
            className="mb-2"
          />
          <h6>Campaña Recurrente</h6>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            Programa envíos repetidos automáticamente
          </p>
        </div>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER STEP 2: DATE/TIME - INTEGRACIÓN DATETIMEPICKER
  // ═══════════════════════════════════════════════════════════════════════
  
  const renderStep2 = () => {
    const handleDateTimeChange = (date: Date) => {
      setScheduledDateTime(date);
      
      // Usar fecha/hora LOCAL, no UTC
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      setFormData({
        ...formData,
        schedule: {
          ...formData.schedule,
          date: `${year}-${month}-${day}`,
          time: `${hours}:${minutes}`
        }
      });
    };

    return (
      <div>
        <h5 className="mb-3">
          {formData.type === 'single' ? 'Fecha y Hora del Envío' : 'Hora de Inicio'}
        </h5>

        <DateTimePicker
          value={scheduledDateTime}
          onChange={handleDateTimeChange}
          minDate={new Date()}
        />
        
        {/* Timezone Selector - componente separado */}
        <div style={{ marginTop: '16px' }}>
          <TimeZoneSelector
            value={formData.schedule.timezone}
            onChange={(tz) => setFormData({
              ...formData,
              schedule: { ...formData.schedule, timezone: tz }
            })}
            showPreview={false}
          />
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER STEP 3: RECIPIENTS - INTEGRACIÓN CONTACTSELECTORMODAL
  // ═══════════════════════════════════════════════════════════════════════
  
  const renderStep3 = () => {
    const selectedContacts = getSelectedContactsData();
    
    return (
      <div>
        <h5 className="mb-3">Destinatarios</h5>
        
        <div
          style={{
            padding: '24px',
            borderRadius: '12px',
            border: '2px dashed var(--bs-border-color)',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onClick={() => setShowContactModal(true)}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#2ca799';
            e.currentTarget.style.backgroundColor = 'rgba(44, 167, 153, 0.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--bs-border-color)';
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          {selectedContacts.length === 0 ? (
            <>
              <FontAwesomeIcon
                icon={faUsers}
                style={{ fontSize: '3rem', color: 'var(--bs-secondary)', opacity: 0.3, marginBottom: '16px' }}
              />
              <h6 style={{ fontWeight: 600, marginBottom: '8px' }}>
                No hay destinatarios seleccionados
              </h6>
              <p style={{ fontSize: '0.875rem', color: 'var(--bs-secondary)', marginBottom: 0 }}>
                Haz clic para seleccionar contactos
              </p>
            </>
          ) : (
            <>
              <div style={{ fontSize: '2.5rem', fontWeight: 600, color: '#2ca799', marginBottom: '8px' }}>
                {selectedContacts.length}
              </div>
              <p style={{ fontSize: '0.875rem', color: 'var(--bs-secondary)', marginBottom: '16px' }}>
                {selectedContacts.length === 1 ? 'destinatario seleccionado' : 'destinatarios seleccionados'}
              </p>
              <div style={{
                maxHeight: '150px',
                overflowY: 'auto',
                padding: '12px',
                backgroundColor: 'rgba(44, 167, 153, 0.05)',
                borderRadius: '8px',
                marginBottom: '12px'
              }}>
                {selectedContacts.slice(0, 5).map(contact => (
                  <div key={contact.id} style={{ padding: '4px 0', fontSize: '0.813rem', textAlign: 'left' }}>
                    <strong>{contact.name}</strong> - {contact.phone}
                  </div>
                ))}
                {selectedContacts.length > 5 && (
                  <div style={{ padding: '4px 0', fontSize: '0.813rem', color: 'var(--bs-secondary)' }}>
                    ... y {selectedContacts.length - 5} más
                  </div>
                )}
              </div>
              <Badge bg="primary" style={{ backgroundColor: '#2ca799', fontSize: '0.875rem', padding: '8px 12px' }}>
                Haz clic para cambiar selección
              </Badge>
            </>
          )}
        </div>

        <ContactSelectorModal
          show={showContactModal}
          onHide={() => setShowContactModal(false)}
          contacts={contacts}
          selectedContactIds={selectedContactIds}
          onContactToggle={toggleContactSelection}
          onSelectAll={selectAll}
          onClearAll={clearSelection}
          groups={groups}
          tags={tags}
          onConfirm={(selectedIds) => {
            setFormData({
              ...formData,
              recipients: {
                contactIds: Array.from(selectedIds),
                count: selectedIds.size
              }
            });
            setShowContactModal(false);
          }}
        />
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER STEP 4: MESSAGE - INTEGRACIÓN MESSAGECOMPOSER COMPLETO
  // ═══════════════════════════════════════════════════════════════════════
  
  const renderStep4 = () => {
    // Calcular riesgo básico
    const getRiskData = () => {
      if (!formData.message.text || formData.recipients.count === 0) return null;
      
      const hasLinks = /https?:\/\//.test(formData.message.text);
      const hasEmojis = /[\u{1F600}-\u{1F64F}]/u.test(formData.message.text);
      const messageLength = formData.message.text.length;
      
      let score = 100;
      if (hasLinks) score -= 15;
      if (messageLength > 500) score -= 10;
      if (hasEmojis) score += 5;
      if (formData.recipients.count > 100) score -= 10;
      
      score = Math.max(0, Math.min(100, score));
      
      const level: 'low' | 'medium' | 'high' | 'critical' = 
        score >= 80 ? 'low' :
        score >= 60 ? 'medium' :
        score >= 40 ? 'high' : 'critical';
      
      const mainIssues: string[] = [];
      if (hasLinks) mainIssues.push('El mensaje contiene enlaces, puede aumentar el riesgo de spam');
      if (messageLength > 500) mainIssues.push('Mensaje muy largo, considera acortarlo');
      if (formData.recipients.count > 100) mainIssues.push('Alto volumen de envíos, procede con cuidado');
      
      return {
        score,
        level,
        flagProbability: 100 - score,
        mainIssues
      };
    };
    
    // Obtener contacto de muestra para preview
    const selectedContacts = getSelectedContactsData();
    const sampleContact = selectedContacts.length > 0 ? {
      name: selectedContacts[0].name,
      company: selectedContacts[0].company,
      position: selectedContacts[0].positon,
      email: selectedContacts[0].email
    } : undefined;

    return (
      <div>
        <h5 className="mb-3">Mensaje</h5>

        <MessageComposer
  value={formData.message.text}
  onChange={(text) => setFormData({
    ...formData,
    message: { ...formData.message, text }
  })}
  riskData={getRiskData()}
  showRiskIndicator={true}
  contactCount={formData.recipients.count}
  sampleContact={sampleContact} // ✅ YA está pasado correctamente
  maxLength={1000}
  minLength={10}
  phoneId={user._id}  // ← AGREGAR ESTO
  apiToken={getHatateToken()}  // ← AGREGAR ESTO (si tienes la función)
/>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER STEP 5: CHANNEL
  // ═══════════════════════════════════════════════════════════════════════
  
  const renderStep5 = () => {
    const channels: Array<{ id: ChannelType; icon: any; color: string; label: string }> = [
      { id: 'whatsapp', icon: faWhatsapp, color: '#25D366', label: 'WhatsApp' },
      { id: 'instagram', icon: faInstagram, color: '#E4405F', label: 'Instagram' },
      { id: 'facebook', icon: faFacebookF, color: '#1877F2', label: 'Facebook' },
      { id: 'telegram', icon: faTelegram, color: '#0088CC', label: 'Telegram' }
    ];

    return (
      <div>
        <h5 className="mb-3">Canal de Envío</h5>

        <div className="d-flex flex-wrap gap-3">
          {channels.map((channel) => (
            <div
              key={channel.id}
              onClick={() => setFormData({ ...formData, channel: channel.id })}
              style={{
                flex: '1 1 calc(50% - 12px)',
                minWidth: '200px',
                padding: '20px',
                borderRadius: '12px',
                border: `2px solid ${formData.channel === channel.id ? channel.color : 'var(--bs-border-color)'}`,
                backgroundColor: formData.channel === channel.id ? `${channel.color}20` : 'var(--bs-body-bg)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                textAlign: 'center'
              }}
            >
              <FontAwesomeIcon 
                icon={channel.icon} 
                size="3x" 
                style={{ color: channel.color }}
                className="mb-2"
              />
              <h6>{channel.label}</h6>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER STEP 6: CONFIRMATION
  // ═══════════════════════════════════════════════════════════════════════
  
  const renderStep6 = () => {
    const channelConfig = {
      whatsapp: { icon: faWhatsapp, color: '#25D366', label: 'WhatsApp' },
      instagram: { icon: faInstagram, color: '#E4405F', label: 'Instagram' },
      facebook: { icon: faFacebookF, color: '#1877F2', label: 'Facebook' },
      telegram: { icon: faTelegram, color: '#0088CC', label: 'Telegram' }
    };

    const channel = channelConfig[formData.channel];
    const preview = generatePreview(formData.schedule.date, formData.schedule.time, formData.schedule.timezone);

    return (
      <div>
        <h5 className="mb-3">Confirmación</h5>

        {formData.type === 'recurring' && (
          <Form.Group className="mb-3">
            <Form.Label>Nombre de la campaña</Form.Label>
            <Form.Control
              type="text"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder="Ej: Newsletter Semanal"
              style={{ borderRadius: '8px' }}
            />
          </Form.Group>
        )}

        <div
          style={{
            padding: '20px',
            borderRadius: '12px',
            backgroundColor: 'var(--bs-secondary-bg)',
            border: '1px solid var(--bs-border-color)'
          }}
        >
          <div className="mb-3">
            <strong>Tipo:</strong>{' '}
            <Badge bg={formData.type === 'single' ? 'primary' : 'info'}>
              {formData.type === 'single' ? 'Envío Único' : 'Campaña Recurrente'}
            </Badge>
          </div>

          <div className="mb-3">
            <strong>Programado para:</strong>
            <div className="text-muted">{preview}</div>
          </div>

          <div className="mb-3">
            <strong>Canal:</strong>{' '}
            <Badge bg="none" style={{ backgroundColor: `${channel.color}20`, color: channel.color, border: `1px solid ${channel.color}` }}>
              <FontAwesomeIcon icon={channel.icon} className="me-1" />
              {channel.label}
            </Badge>
          </div>

          <div className="mb-3">
            <strong>Destinatarios:</strong>{' '}
            {formData.recipients.count}
          </div>

          <div className="mb-0">
            <strong>Mensaje:</strong>
            <div
              style={{
                marginTop: '8px',
                padding: '12px',
                borderRadius: '8px',
                backgroundColor: 'var(--bs-body-bg)',
                border: '1px solid var(--bs-border-color)',
                whiteSpace: 'pre-wrap',
                fontSize: '0.9rem'
              }}
            >
              {formData.message.text}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER CURRENT STEP
  // ═══════════════════════════════════════════════════════════════════════
  
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      default: return null;
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // MAIN RENDER - USANDO PORTAL
  // ═══════════════════════════════════════════════════════════════════════
  
  if (!show) return null;

  const modalContent = (
    <>
      {/* Backdrop custom */}
      <div
        onClick={handleClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 99998,
          backdropFilter: 'blur(2px)'
        }}
      />
      
      {/* Modal custom */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 99999,
          width: '90%',
          maxWidth: '800px',
          maxHeight: '90vh',
          backgroundColor: 'var(--bs-body-bg)',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--bs-border-color)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <h5 className="mb-0">
            {event ? 'Editar Envío' : 'Programar Nuevo Envío'}
          </h5>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: 'var(--bs-secondary-color)',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bs-secondary-bg)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: '24px',
            overflowY: 'auto',
            flex: 1
          }}
        >
          {renderProgress()}
          
          {error && (
            <Alert variant="danger" style={{ borderRadius: '8px' }} className="mb-3">
              <FontAwesomeIcon icon={faTimes} className="me-2" />
              {error}
            </Alert>
          )}

          {renderCurrentStep()}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '20px 24px',
            borderTop: '1px solid var(--bs-border-color)',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}
        >
          {currentStep > 1 && (
            <Button
              variant="outline-secondary"
              onClick={handleBack}
              disabled={isSubmitting}
              style={{ borderRadius: '8px' }}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Atrás
            </Button>
          )}

          {currentStep < 6 ? (
            <Button
              onClick={handleNext}
              disabled={isSubmitting}
              style={{
                backgroundColor: '#2ca799',
                border: 'none',
                borderRadius: '8px'
              }}
            >
              Siguiente
              <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={{
                backgroundColor: '#2ca799',
                border: 'none',
                borderRadius: '8px'
              }}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Creando...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheck} className="me-2" />
                  {formData.type === 'single' ? 'Crear Envío' : 'Crear Campaña'}
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </>
  );

  // Renderizar usando Portal directamente en document.body
  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

export default ScheduleEventModal;